import "@/i18n/config";
import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { RouterProvider, createBrowserRouter, ScrollRestoration, Outlet, Navigate } from "react-router-dom";
import { AuthProvider } from "@/contexts/AuthContext";
import ProtectedRoute from "@/components/auth/ProtectedRoute";
import { InstallBanner, OfflineIndicator, SyncStatusIndicator } from "@/components/pwa";
import { GlobalErrorBoundary } from "@/components/ui/error-boundary";
import Index from "./pages/Index";

import AdminVerify from "@/pages/admin/AdminVerify";
import Login from "./pages/Login";
import Signup from "./pages/Signup";
import ForgotPassword from "./pages/ForgotPassword";
import ResetPassword from "./pages/ResetPassword";
import VerifyEmail from "./pages/VerifyEmail";
import Dashboard from "./pages/Dashboard";
import Scripts from "./pages/Scripts";
import Editor from "./pages/Editor";
import Teleprompter from "./pages/Teleprompter";
// Checkout 頁面已移除，改為直接跳轉 Stripe checkout
import PaymentSuccess from "./pages/PaymentSuccess";
import PaymentFailed from "./pages/PaymentFailed";
import SubscriptionSettings from "./pages/SubscriptionSettings";
import ProfileSettings from "./pages/ProfileSettings";
import SecuritySettings from "./pages/SecuritySettings";
import ConnectionsSettings from "./pages/ConnectionsSettings";
import Privacy from "./pages/Privacy";
import Terms from "./pages/Terms";
import Contact from "./pages/Contact";
import Install from "./pages/Install";
import Reminders from "./pages/Reminders";
import NotFound from "./pages/NotFound";
import { DashboardRoot } from "@/components/dashboard/DashboardRoot";
import { AdminGuard } from "@/components/admin/AdminGuard";
import { AdminLayout } from "@/components/admin/AdminLayout";
import AdminDashboard from "@/pages/admin/AdminDashboard";
import AdminUsers from "@/pages/admin/AdminUsers";
import AdminUserDetail from "@/pages/admin/AdminUserDetail";
import AdminSettings from "@/pages/admin/AdminSettings";
import AdminSales from "@/pages/admin/AdminSales";
import AdminPromoCodes from "@/pages/admin/AdminPromoCodes";
import AdminPromoCodeDetail from "@/pages/admin/AdminPromoCodeDetail";
import AdminTickets from "@/pages/admin/AdminTickets";
import AdminTicketDetail from "@/pages/admin/AdminTicketDetail";
import AdminContent from "@/pages/admin/AdminContent";
import AdminFinance from "@/pages/admin/AdminFinance";
import AdminManagement from "@/pages/admin/AdminManagement";
import AdminSubscriptions from "@/pages/admin/AdminSubscriptions";


const queryClient = new QueryClient();

// Layout component with scroll restoration and PWA components
const Layout = () => (
  <>
    <OfflineIndicator />
    <SyncStatusIndicator />
    <ScrollRestoration />
    <Outlet />
    <InstallBanner />
  </>
);

// Create router with automatic scroll restoration
const router = createBrowserRouter([
  {
    element: <Layout />,
    children: [
      {
        path: "/",
        element: <Index />,
      },
      {
        path: "/login",
        element: <Login />,
      },
      {
        path: "/signup",
        element: <Signup />,
      },
      {
        path: "/forgot-password",
        element: <ForgotPassword />,
      },
      {
        path: "/verify-email",
        element: <VerifyEmail />,
      },
      {
        path: "/reset-password",
        element: <ResetPassword />,
      },
      {
        // /checkout 路由重定向到首頁 pricing section
        path: "/checkout",
        element: <Navigate to="/#pricing" replace />,
      },
      {
        path: "/payment/success",
        element: (
          <ProtectedRoute>
            <PaymentSuccess />
          </ProtectedRoute>
        ),
      },
      {
        path: "/payment/failed",
        element: (
          <ProtectedRoute>
            <PaymentFailed />
          </ProtectedRoute>
        ),
      },
      {
        element: (
          <ProtectedRoute>
            <DashboardRoot />
          </ProtectedRoute>
        ),
        children: [
          {
            path: "/dashboard",
            element: <Dashboard />,
          },
          {
            path: "/scripts",
            element: <Scripts />,
          },
          {
            path: "/reminders",
            element: <Reminders />,
          },
          {
            path: "/settings",
            element: <Navigate to="/settings/profile" replace />,
          },
          {
            path: "/settings/profile",
            element: <ProfileSettings />,
          },
          {
            path: "/settings/subscription",
            element: <SubscriptionSettings />,
          },
          {
            path: "/settings/security",
            element: <SecuritySettings />,
          },
          {
            path: "/settings/connections",
            element: <ConnectionsSettings />,
          },
        ],
      },
      {
        path: "/editor/:id",
        element: (
          <ProtectedRoute>
            <Editor />
          </ProtectedRoute>
        ),
      },
      {
        path: "/teleprompter/:id",
        element: (
          <ProtectedRoute>
            <Teleprompter />
          </ProtectedRoute>
        ),
      },
      {
        path: "/privacy",
        element: <Privacy />,
      },
      {
        path: "/terms",
        element: <Terms />,
      },
      {
        path: "/contact",
        element: <Contact />,
      },
      {
        path: "/install",
        element: <Install />,
      },
      {
        path: "/admin/verify",
        element: (
          <ProtectedRoute>
            <AdminVerify />
          </ProtectedRoute>
        ),
      },
      // Admin Routes
      {
        path: "/admin",
        element: (
          <AdminGuard>
            <AdminLayout />
          </AdminGuard>
        ),
        children: [
          {
            index: true,
            element: <AdminDashboard />,
          },
          {
            path: "users",
            element: <AdminUsers />,
          },
          {
            path: "users/:id",
            element: <AdminUserDetail />,
          },
          {
            path: "settings",
            element: <AdminSettings />,
          },
          {
            path: "sales",
            element: <AdminSales />,
          },
          {
            path: "finance",
            element: <AdminFinance />,
          },
          {
            path: "promo-codes",
            element: <AdminPromoCodes />,
          },
          {
            path: "promo-codes/:id",
            element: <AdminPromoCodeDetail />,
          },
          {
            path: "tickets",
            element: <AdminTickets />,
          },
          {
            path: "tickets/:id",
            element: <AdminTicketDetail />,
          },
          {
            path: "content",
            element: <AdminContent />,
          },
          {
            path: "management",
            element: <AdminManagement />,
          },
          {
            path: "subscriptions",
            element: <AdminSubscriptions />,
          },
          // Catch-all for admin to dashboard
          {
            path: "*",
            element: <AdminDashboard />,
          },
        ],
      },
      {
        path: "*",
        element: <NotFound />,
      },
    ],
  },
], {
  future: {
    // @ts-expect-error React Router v7 future flag typing
    v7_startTransition: true,
    v7_relativeSplatPath: true,
  }
});

const App = () => (
  <GlobalErrorBoundary>
    <QueryClientProvider client={queryClient}>
      <AuthProvider>
        <TooltipProvider>
          <Toaster />
          <Sonner />
          <RouterProvider router={router} />
        </TooltipProvider>
      </AuthProvider>
    </QueryClientProvider>
  </GlobalErrorBoundary>
);

export default App;
